import { ref, computed, nextTick } from 'vue'

export function usePaymentTxnScan({ getPaymentMethod, onApplyScan, showToast, onQrContent }) {
const scannedReceiverDisplay = ref('')

const showTxnScanModal = ref(false)
const showTxnScanSourcePicker = ref(false)
const showTxnLiveCameraScan = ref(false)
const isScanningTransaction = ref(false)
const txnLiveScanProcessing = ref(false)
const txnLiveScanPurpose = ref('ocr')
const txnScanMode = ref('chooser')
const txnScanFileInput = ref(null)
const txnScanCameraInput = ref(null)
const txnScanVideo = ref(null)
const txnLiveScanVideo = ref(null)
let txnScanCameraStream = null
let tesseractWorker = null
let txnLiveScanTimer = null
let txnLiveScanBusy = false
let txnPendingQrDecode = false
let txnQrScanRafId = null
let jsQrModule = null
let txnBarcodeDetector = null

const isCbePaymentSelected = computed(() => isCbeMethod(normalizePaymentMethodName(getPaymentMethod())))
const isTelebirrPaymentSelected = computed(() => isTelebirrMethod(normalizePaymentMethodName(getPaymentMethod())))

const TELEBIRR_RECEIVER_PHONE = '0911111111'

const TXN_VALUE_NOISE = /^(?:transaction\s+)?(?:successful|success|complete|completed|failed|pending|approved|paid|sent|received|processed)\b/i
const INVALID_TXN_VALUES = new Set([
  'successful', 'success', 'completed', 'complete', 'failed', 'pending',
  'approved', 'paid', 'sent', 'received', 'processed', 'telebirr', 'cbe',
  'number', 'amount', 'date', 'time', 'total', 'balance', 'etb', 'birr',
  'utility', 'payment', 'electricity', 'ethiopian', 'finished', 'transfer',
  'commercial', 'bank', 'reason', 'debit', 'credit', 'charge', 'vat',
])

const TELEBIRR_TXN_NUMBER_PATTERNS = [
  /transac[tlo1i0n]+\s+num[b8o0]?[e3a]?r\.?\s*[:-]?\s*([A-Z0-9]{8,12})\b/gi,
  /txn\s+num[b8o0]?[e3a]?r\.?\s*[:-]?\s*([A-Z0-9]{8,12})\b/gi,
]

const CBE_TXN_ID_PATTERNS = [
  /(?:with\s+)?transaction\s*[i1l|]?\s*d\s*[:-]?\s*(FT[A-Z0-9]{8,16})\b/gi,
  /transaction\s*[i1l|]?\s*d\s*[:-]?\s*(FT[A-Z0-9]{8,16})\b/gi,
  /\bID\s*:\s*(FT[A-Z0-9]{8,16})\b/gi,
  /txn\s*[i1l|]?\s*d\s*[:-]?\s*(FT[A-Z0-9]{8,16})\b/gi,
  /\b(FT[A-Z0-9]{8,16})\b/g,
]

function normalizePaymentMethodName(method) {
  return String(method?.name || method || '').trim().toLowerCase()
}

function isTelebirrMethod(name) {
  return /tele\s*birr|telebirr|telet\s*birr/i.test(name)
}

function isCbeMethod(name) {
  return /\bcbe\b|commercial\s*bank|bank\s*of\s*ethiopia/i.test(name)
}

function buildTxnLabelConfig(term) {
  const flexibleTerm = term.replace(/\\s\+/g, '\\s*')
  return {
    label: new RegExp(flexibleTerm, 'i'),
  }
}

function getTxnLabelConfigs(paymentMethod) {
  const name = normalizePaymentMethodName(paymentMethod)
  if (isTelebirrMethod(name)) {
    return [
      'transac[tlo1i0n]+\\s+num[b8o0]?[e3a]?r',
      'transaction\\s+num[b8]er',
      'transaction\\s+no\\.?(?!\\w)',
      'txn\\s+num[b8]er',
      'txn\\s+no\\.?(?!\\w)',
    ].map(buildTxnLabelConfig)
  }
  if (isCbeMethod(name)) {
    return [
      'with\\s+transaction\\s+[i1l|]\\s*d',
      'transaction\\s+[i1l|]\\s*d',
      'transaction\\s+id',
      'transactionid',
      'txn\\s+[i1l|]\\s*d',
      'txn\\s+id',
    ].map(buildTxnLabelConfig)
  }
  return [
    'transaction\\s+number', 'transaction\\s+id', 'transaction\\s+no',
    'reference\\s+number', 'reference\\s+id', 'reference\\s+no',
    'ref\\.?\\s+no', 'ref\\.?\\s+number', 'ref\\.?\\s+id',
    'receipt\\s+number', 'receipt\\s+no', 'receipt\\s+id',
    'trace\\s+number', 'trace\\s+no', 'trace\\s+id',
    'txn\\s+id', 'txn\\s+number', 'txn\\s+no',
  ].map(buildTxnLabelConfig)
}

function normalizeOcrText(ocrText) {
  return String(ocrText || '')
    .replace(/\u00A0/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[ï¼šï¹•ï¼›]/g, ':')
    .replace(/[|Â¦]/g, ' ')
    .replace(/\r/g, '\n')
}

function collapseOcrText(text) {
  return normalizeOcrText(text).replace(/\s+/g, ' ').trim()
}

function cleanTxnId(value) {
  return String(value || '').trim().replace(/[^\w-]/g, '')
}

function isValidTxnValue(value) {
  const cleaned = cleanTxnId(value)
  if (!cleaned || cleaned.length < 4) return false
  if (INVALID_TXN_VALUES.has(cleaned.toLowerCase())) return false
  if (/^\d{1,2}$/.test(cleaned)) return false
  return true
}

function isValidTelebirrTxnValue(value) {
  const cleaned = cleanTxnId(value).toUpperCase()
  if (!isValidTxnValue(cleaned)) return false
  if (/^FT[A-Z0-9]{6,}$/.test(cleaned)) return false
  if (!/^[A-Z0-9]{8,12}$/.test(cleaned)) return false
  if (!/[A-Z]/.test(cleaned) || !/[0-9]/.test(cleaned)) return false
  return true
}

function isTxnIdValidForPaymentMethod(txnId, paymentMethod) {
  const cleaned = cleanTxnId(txnId).toUpperCase()
  if (!cleaned) return false
  const name = normalizePaymentMethodName(paymentMethod)
  if (isTelebirrMethod(name)) return isValidTelebirrTxnValue(cleaned)
  if (isCbeMethod(name)) return isValidCbeTxnValue(cleaned)
  return isValidTxnValue(cleaned)
}

function detectPaymentScreenshotMismatch(ocrText, paymentMethod) {
  const name = normalizePaymentMethodName(paymentMethod)
  const collapsed = collapseOcrText(ocrText)
  if (!collapsed) return ''

  const hasTelebirrMarkers = /tele\s*birr|telebirr|telet\s*birr/i.test(collapsed) ||
    /(?:transac|txn)\s+num[b8]/i.test(collapsed)
  const hasCbeMarkers = /\b(?:cbe|commercial\s*bank|bank\s*of\s*ethiopia)\b/i.test(collapsed) ||
    /transaction\s*[i1l|]?\s*d\s*[:-]?\s*FT/i.test(collapsed) ||
    /\bFT[A-Z0-9]{8,16}\b/i.test(collapsed)

  if (isTelebirrMethod(name) && hasCbeMarkers && !hasTelebirrMarkers) {
    return 'This image looks like a CBE payment screenshot. Please upload a Telebirr confirmation.'
  }
  if (isCbeMethod(name) && hasTelebirrMarkers && !hasCbeMarkers) {
    return 'This image looks like a Telebirr payment screenshot. Please upload a CBE confirmation.'
  }
  return ''
}

function isValidCbeTxnValue(value) {
  const cleaned = cleanTxnId(value).toUpperCase()
  if (!isValidTxnValue(cleaned)) return false
  return /^FT[A-Z0-9]{8,16}$/.test(cleaned)
}

function pickTelebirrTxnCandidate(text) {
  if (!text || TXN_VALUE_NOISE.test(text)) return ''
  const tokens = String(text).match(/\b[A-Z0-9]{8,12}\b/gi) || []
  const valid = tokens.map((token) => cleanTxnId(token).toUpperCase()).filter(isValidTelebirrTxnValue)
  if (!valid.length) return ''
  return valid.sort((a, b) => {
    const aLetters = (a.match(/[A-Z]/g) || []).length
    const bLetters = (b.match(/[A-Z]/g) || []).length
    if (aLetters !== bLetters) return bLetters - aLetters
    return b.length - a.length
  })[0]
}

function pickCbeTxnCandidate(text) {
  if (!text || TXN_VALUE_NOISE.test(text)) return ''
  const tokens = String(text).match(/\bFT[A-Z0-9]{8,16}\b/gi) || []
  const valid = tokens.map((token) => cleanTxnId(token).toUpperCase()).filter(isValidCbeTxnValue)
  if (!valid.length) return ''
  return valid.sort((a, b) => b.length - a.length)[0]
}

function pickBestTxnCandidate(text, paymentMethod = null) {
  if (!text || TXN_VALUE_NOISE.test(text)) return ''
  const name = normalizePaymentMethodName(paymentMethod)
  if (isTelebirrMethod(name)) return pickTelebirrTxnCandidate(text)
  if (isCbeMethod(name)) return pickCbeTxnCandidate(text)

  const tokens = String(text).match(/[A-Za-z0-9][A-Za-z0-9-]{2,}/g) || []
  const valid = tokens.map(cleanTxnId).filter(isValidTxnValue)
  if (!valid.length) return ''
  return valid.sort((a, b) => b.length - a.length)[0]
}

function cleanReceiverName(name) {
  return String(name || '')
    .replace(/\s+/g, ' ')
    .replace(/^[:.\-\s]+/, '')
    .replace(/[:.\-\s]+$/, '')
    .trim()
}

function extractCbeReceiverDisplay(ocrText) {
  if (!ocrText) return ''
  const collapsed = collapseOcrText(ocrText)
  const accountToken = '(?:ETB|EIB|ET8)\\s*[\\-:]?\\s*(\\d{4})'
  const patterns = [
    new RegExp(`\\bfor\\s+([A-Za-z][A-Za-z0-9&.,'/\\- ]{2,80}?)\\s+${accountToken}\\s+on\\b`, 'i'),
    new RegExp(`debited\\s+from\\s+.+?\\s+${accountToken}\\s+for\\s+([A-Za-z][A-Za-z0-9&.,'/\\- ]{2,80}?)\\s+${accountToken}\\b`, 'i'),
  ]

  const first = collapsed.match(patterns[0])
  if (first?.[1] && first?.[2]) {
    const name = cleanReceiverName(first[1])
    if (name) return `${name} ETB-${first[2]}`
  }

  const second = collapsed.match(patterns[1])
  if (second?.[2] && second?.[3]) {
    const name = cleanReceiverName(second[2])
    if (name) return `${name} ETB-${second[3]}`
  }
  return ''
}

const TELEBIRR_RECEIVER_LABEL_RE = /trans(?:fer|action|ac[tlo1i0n]+)\s+(?:to|t0)\b/i
const TELEBIRR_RECEIVER_LABEL_STRIP_RE = /trans(?:fer|action|ac[tlo1i0n]+)\s+(?:to|t0)\s*[:-]?\s*/i
const TELEBIRR_RECEIVER_LABEL_ONLY_RE = /^trans(?:fer|action|ac[tlo1i0n]+)\s+(?:to|t0)\s*[:-]?\s*$/i
const TELEBIRR_RECEIVER_STOP_RE = /\b(?:transaction\s+(?:number|num|type|time)|txn\s+num|contract|customer|recharge)\b/i
const TELEBIRR_RECEIVER_BEFORE_NUMBER_RE = /trans(?:fer|action|ac[tlo1i0n]+)\s+(?:to|t0)\s*[:-]?\s*(.+?)(?=\s+trans(?:fer|action|ac[tlo1i0n]+)\s+(?:number|num)\b)/i
const TELEBIRR_FIELD_LABEL_RE = /^(?:trans(?:fer|action|ac[tlo1i0n]+)\s+(?:time|type|to|t0|number|num)|contract\s+account\s+number|customer\s+name|recharge\s+token)\b\s*[:-]?\s*$/i

function isTelebirrReceiverTypeLine(line) {
  return /trans(?:fer|action|ac[tlo1i0n]+)\s+type\b/i.test(collapseOcrText(line))
}

function isTelebirrPureFieldLabelLine(line) {
  return TELEBIRR_FIELD_LABEL_RE.test(collapseOcrText(line))
}

function parseTelebirrReceiverNameCandidate(raw) {
  if (!raw) return ''
  const segment = String(raw).split(TELEBIRR_RECEIVER_STOP_RE)[0]
  const name = cleanReceiverName(segment)
  if (!name) return ''
  if (/^(?:type|time|number|num|transaction|txn|transfer)\b/i.test(name)) return ''
  if (/^\d{4}[-/]\d{2}[-/]\d{2}/.test(name)) return ''
  return name
}

function formatTelebirrReceiverDisplay(name) {
  const cleaned = cleanReceiverName(name)
  if (!cleaned) return ''
  return `Transaction To: ${cleaned} ${TELEBIRR_RECEIVER_PHONE}`
}

function extractTelebirrReceiverFromColumnarLayout(lines) {
  const collapsedLines = lines.map((line) => collapseOcrText(line))
  let receiverLabelIdx = -1

  for (let i = 0; i < collapsedLines.length; i++) {
    if (!isTelebirrReceiverLabelLine(lines[i])) continue

    const inline = parseTelebirrReceiverNameCandidate(
      collapsedLines[i].replace(TELEBIRR_RECEIVER_LABEL_STRIP_RE, '').trim()
    )
    if (inline) return inline

    if (TELEBIRR_RECEIVER_LABEL_ONLY_RE.test(collapsedLines[i]) ||
      !collapsedLines[i].replace(TELEBIRR_RECEIVER_LABEL_STRIP_RE, '').trim()) {
      receiverLabelIdx = i
      break
    }
  }

  if (receiverLabelIdx < 0) return ''

  if (receiverLabelIdx + 1 < lines.length && !isTelebirrPureFieldLabelLine(lines[receiverLabelIdx + 1])) {
    const name = parseTelebirrReceiverNameCandidate(collapsedLines[receiverLabelIdx + 1])
    if (name) return name
  }

  let blockStart = receiverLabelIdx
  while (blockStart > 0 && isTelebirrPureFieldLabelLine(lines[blockStart - 1])) {
    blockStart--
  }

  let blockEnd = receiverLabelIdx
  while (blockEnd + 1 < lines.length && isTelebirrPureFieldLabelLine(lines[blockEnd + 1])) {
    blockEnd++
  }

  const receiverOffset = receiverLabelIdx - blockStart
  let valueStart = blockEnd + 1
  while (valueStart < lines.length && isTelebirrPureFieldLabelLine(lines[valueStart])) {
    valueStart++
  }

  const valueIdx = valueStart + receiverOffset
  if (valueIdx < lines.length && !isTelebirrPureFieldLabelLine(lines[valueIdx])) {
    const name = parseTelebirrReceiverNameCandidate(collapsedLines[valueIdx])
    if (name) return name
  }

  return ''
}

function isTelebirrReceiverLabelLine(line) {
  const collapsed = collapseOcrText(line)
  if (isTelebirrReceiverTypeLine(line)) return false
  return TELEBIRR_RECEIVER_LABEL_RE.test(collapsed)
}

function extractTelebirrReceiverDisplay(ocrText) {
  if (!ocrText) return ''
  const collapsed = collapseOcrText(ocrText)

  const beforeNumber = collapsed.match(TELEBIRR_RECEIVER_BEFORE_NUMBER_RE)
  if (beforeNumber?.[1]) {
    const name = parseTelebirrReceiverNameCandidate(beforeNumber[1])
    if (name) return formatTelebirrReceiverDisplay(name)
  }

  const sameLine = collapsed.match(
    /trans(?:fer|action|ac[tlo1i0n]+)\s+(?:to|t0)\s*[:-]?\s*([A-Za-z][A-Za-z0-9&.,'/\- ]{1,80}?)(?=\s+(?:transaction\s+(?:number|num|type|time)|txn\s+num|contract|customer|recharge)\b|$)/i
  )
  if (sameLine?.[1]) {
    const name = parseTelebirrReceiverNameCandidate(sameLine[1])
    if (name) return formatTelebirrReceiverDisplay(name)
  }

  const lines = normalizeOcrText(ocrText).split(/\n+/).map((line) => line.trim()).filter(Boolean)
  for (let i = 0; i < lines.length; i++) {
    const line = collapseOcrText(lines[i])
    if (!isTelebirrReceiverLabelLine(lines[i])) continue

    const inline = parseTelebirrReceiverNameCandidate(line.replace(TELEBIRR_RECEIVER_LABEL_STRIP_RE, '').trim())
    if (inline) return formatTelebirrReceiverDisplay(inline)

    const isLabelOnly = TELEBIRR_RECEIVER_LABEL_ONLY_RE.test(line) ||
      !line.replace(TELEBIRR_RECEIVER_LABEL_STRIP_RE, '').trim()

    if (isLabelOnly && i + 1 < lines.length && !isTelebirrPureFieldLabelLine(lines[i + 1])) {
      const name = parseTelebirrReceiverNameCandidate(collapseOcrText(lines[i + 1]))
      if (name) return formatTelebirrReceiverDisplay(name)
    }
  }

  const columnarName = extractTelebirrReceiverFromColumnarLayout(lines)
  if (columnarName) return formatTelebirrReceiverDisplay(columnarName)

  return ''
}

function extractScannedReceiverDisplay(ocrText, paymentMethod = null) {
  const name = normalizePaymentMethodName(paymentMethod)
  if (isCbeMethod(name)) return extractCbeReceiverDisplay(ocrText)
  if (isTelebirrMethod(name)) return extractTelebirrReceiverDisplay(ocrText)
  return ''
}

function applyExtractedPaymentScan(extracted, receiverDisplay = '') {
  onApplyScan(extracted, receiverDisplay)
  scannedReceiverDisplay.value = receiverDisplay || ''
}

function extractValueAfterLabel(text, label, paymentMethod = null) {
  const collapsed = collapseOcrText(text)
  const match = collapsed.match(label)
  if (!match || match.index == null) return ''
  const after = collapsed.slice(match.index + match[0].length)
  const remainder = after.replace(/^[\s:.#|=-]+/, '').trim()
  if (!remainder) return ''
  return pickBestTxnCandidate(remainder, paymentMethod)
}

function extractTelebirrStandaloneLines(ocrText) {
  const lines = normalizeOcrText(ocrText).split(/\n+/).map((line) => line.trim()).filter(Boolean)
  const candidates = []

  for (const line of lines) {
    const cleaned = cleanTxnId(line).toUpperCase()
    if (!isValidTelebirrTxnValue(cleaned)) continue
    if (cleaned !== collapseOcrText(line).replace(/\s+/g, '').toUpperCase()) continue
    candidates.push(cleaned)
  }

  if (!candidates.length) return ''

  const unique = [...new Set(candidates)]
  const score = (id) => {
    let points = 0
    if (id.length >= 9 && id.length <= 11) points += 3
    if (/[A-Z]{2,}/.test(id) && /[0-9]{2,}/.test(id)) points += 4
    if (/^[A-Z]{2,}[0-9][A-Z0-9]*$/.test(id)) points += 2
    return points
  }

  unique.sort((a, b) => score(b) - score(a))
  return unique[0]
}

function extractTelebirrTransactionId(ocrText) {
  if (!ocrText) return ''
  const collapsed = collapseOcrText(ocrText)

  for (const pattern of TELEBIRR_TXN_NUMBER_PATTERNS) {
    const regex = new RegExp(pattern.source, pattern.flags)
    let match = regex.exec(collapsed)
    while (match) {
      const candidate = cleanTxnId(match[1]).toUpperCase()
      if (isValidTelebirrTxnValue(candidate)) return candidate
      match = regex.exec(collapsed)
    }
  }

  const lines = normalizeOcrText(ocrText).split(/\n+/).map((line) => line.trim()).filter(Boolean)
  for (let i = 0; i < lines.length; i++) {
    const line = collapseOcrText(lines[i])
    if (!/(?:transac|txn)/i.test(line) || !/num/i.test(line)) continue

    const inline = line.match(/(?:transac[tlo1i0n]+|txn)\s+num[b8o0]?[e3a]?r\.?\s*[:-]?\s*([A-Z0-9]{8,12})\b/i)
    if (inline?.[1] && isValidTelebirrTxnValue(inline[1])) {
      return cleanTxnId(inline[1]).toUpperCase()
    }

    for (let j = 1; j <= 3 && i + j < lines.length; j++) {
      const candidate = pickTelebirrTxnCandidate(lines[i + j])
      if (candidate) return candidate
    }
  }

  const standalone = extractTelebirrStandaloneLines(ocrText)
  if (standalone) return standalone

  if (/tele\s*birr|telebirr|(?:transac|txn)\s+num/i.test(collapsed)) {
    return pickTelebirrTxnCandidate(collapsed)
  }
  return ''
}

function extractCbeTransactionId(ocrText) {
  if (!ocrText) return ''
  const collapsed = collapseOcrText(ocrText)

  for (const pattern of CBE_TXN_ID_PATTERNS) {
    const regex = new RegExp(pattern.source, pattern.flags)
    let match = regex.exec(collapsed)
    while (match) {
      const candidate = cleanTxnId(match[1] || match[0]).toUpperCase()
      if (isValidCbeTxnValue(candidate)) return candidate
      match = regex.exec(collapsed)
    }
  }

  const lines = normalizeOcrText(ocrText).split(/\n+/).map((line) => line.trim()).filter(Boolean)
  for (let i = 0; i < lines.length; i++) {
    const line = collapseOcrText(lines[i])

    const idLine = line.match(/^\s*ID\s*:\s*(FT[A-Z0-9]{8,16})\b/i)
    if (idLine?.[1] && isValidCbeTxnValue(idLine[1])) {
      return cleanTxnId(idLine[1]).toUpperCase()
    }

    if (/transaction/i.test(line)) {
      const inline = line.match(/(?:with\s+)?transaction\s*[i1l|]?\s*d\s*[:-]?\s*(FT[A-Z0-9]{8,16})\b/i)
      if (inline?.[1] && isValidCbeTxnValue(inline[1])) {
        return cleanTxnId(inline[1]).toUpperCase()
      }

      for (let j = 1; j <= 2 && i + j < lines.length; j++) {
        const nextLine = collapseOcrText(lines[i + j])
        const splitId = nextLine.match(/^\s*ID\s*:\s*(FT[A-Z0-9]{8,16})\b/i)
        if (splitId?.[1] && isValidCbeTxnValue(splitId[1])) {
          return cleanTxnId(splitId[1]).toUpperCase()
        }
        const candidate = pickCbeTxnCandidate(lines[i + j])
        if (candidate) return candidate
      }
    }
  }

  return pickCbeTxnCandidate(collapsed)
}

function extractTransactionIdFromOcrText(ocrText, paymentMethod = null) {
  if (!ocrText) return ''
  const name = normalizePaymentMethodName(paymentMethod)

  if (isTelebirrMethod(name)) {
    const telebirrId = extractTelebirrTransactionId(ocrText)
    if (telebirrId) return telebirrId
  }
  if (isCbeMethod(name)) {
    const cbeId = extractCbeTransactionId(ocrText)
    if (cbeId) return cbeId
  }

  const labelConfigs = getTxnLabelConfigs(paymentMethod)
  const normalized = normalizeOcrText(ocrText)
  const lines = normalized.split(/\n+/).map((line) => line.trim()).filter(Boolean)
  const collapsedAll = collapseOcrText(normalized)

  const searchTexts = [collapsedAll, ...lines.map(collapseOcrText)]
  for (let i = 0; i < lines.length - 1; i++) {
    searchTexts.push(collapseOcrText(`${lines[i]} ${lines[i + 1]}`))
    if (i + 2 < lines.length) {
      searchTexts.push(collapseOcrText(`${lines[i]} ${lines[i + 1]} ${lines[i + 2]}`))
    }
  }

  for (const text of searchTexts) {
    for (const { label } of labelConfigs) {
      const found = extractValueAfterLabel(text, label, paymentMethod)
      if (found) return found
    }
  }

  for (let i = 0; i < lines.length; i++) {
    for (const { label } of labelConfigs) {
      if (!label.test(collapseOcrText(lines[i]))) continue
      for (let j = 1; j <= 3 && i + j < lines.length; j++) {
        const candidate = pickBestTxnCandidate(lines[i + j], paymentMethod)
        if (candidate) return candidate
      }
    }
  }

  return ''
}

function getTxnScanErrorMessage(paymentMethod) {
  const name = normalizePaymentMethodName(paymentMethod)
  if (isTelebirrMethod(name)) {
    return 'Could not find Transaction Number in the image. Try a clearer screenshot.'
  }
  if (isCbeMethod(name)) {
    return 'Could not find Transaction ID in the image. Try a clearer screenshot.'
  }
  return 'Could not find a transaction reference in the image. Try a clearer screenshot.'
}

function getTxnQrScanErrorMessage(paymentMethod) {
  const name = normalizePaymentMethodName(paymentMethod)
  if (isCbeMethod(name)) {
    return 'Could not find CBE transaction ID in QR code. Try a clearer image.'
  }
  return 'Could not find a transaction reference in QR code.'
}

async function getJsQr() {
  if (!jsQrModule) {
    jsQrModule = (await import('jsqr')).default
  }
  return jsQrModule
}

async function decodeQrFromCanvas(canvas) {
  const jsQR = await getJsQr()
  const variants = getQrCanvasVariants(canvas)
  for (const target of variants) {
    const ctx = target.getContext('2d')
    if (!ctx) continue
    const imageData = ctx.getImageData(0, 0, target.width, target.height)
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'attemptBoth',
    })
    if (code?.data) return code.data
  }
  return ''
}

function getQrCanvasVariants(canvas) {
  const variants = [canvas]
  const width = canvas.width
  const height = canvas.height
  const maxSize = 900
  const longest = Math.max(width, height)
  if (longest > maxSize) {
    const scale = maxSize / longest
    const scaled = document.createElement('canvas')
    scaled.width = Math.round(width * scale)
    scaled.height = Math.round(height * scale)
    const ctx = scaled.getContext('2d')
    if (ctx) {
      ctx.drawImage(canvas, 0, 0, scaled.width, scaled.height)
      variants.push(scaled)
    }
  }
  const cropSize = Math.min(width, height) * 0.75
  const cropX = (width - cropSize) / 2
  const cropY = (height - cropSize) / 2
  const cropped = document.createElement('canvas')
  cropped.width = Math.round(cropSize)
  cropped.height = Math.round(cropSize)
  const cropCtx = cropped.getContext('2d')
  if (cropCtx) {
    cropCtx.drawImage(canvas, cropX, cropY, cropSize, cropSize, 0, 0, cropped.width, cropped.height)
    variants.push(cropped)
  }
  return variants
}

async function getTxnBarcodeDetector() {
  if (txnBarcodeDetector) return txnBarcodeDetector
  if (typeof window === 'undefined' || !('BarcodeDetector' in window)) return null
  try {
    txnBarcodeDetector = new window.BarcodeDetector({ formats: ['qr_code'] })
    return txnBarcodeDetector
  } catch (err) {
    console.warn('BarcodeDetector unavailable:', err)
    return null
  }
}

async function decodeQrFromVideoFrame(video) {
  const canvas = captureVideoFrameCanvas(video)
  if (!canvas) return ''
  const detector = await getTxnBarcodeDetector()
  if (detector) {
    try {
      const bitmap = await createImageBitmap(canvas)
      const barcodes = await detector.detect(bitmap)
      bitmap.close?.()
      const data = barcodes?.[0]?.rawValue
      if (data) return data
    } catch (err) {
      console.warn('BarcodeDetector failed, falling back to jsQR:', err)
    }
  }
  return decodeQrFromCanvas(canvas)
}

async function waitForTxnVideoReady(video, timeoutMs = 6000) {
  if (video.videoWidth && video.videoHeight) return
  await new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Camera preview timed out.')), timeoutMs)
    const onReady = () => {
      if (!video.videoWidth || !video.videoHeight) return
      clearTimeout(timeout)
      video.removeEventListener('loadedmetadata', onReady)
      video.removeEventListener('playing', onReady)
      resolve()
    }
    video.addEventListener('loadedmetadata', onReady)
    video.addEventListener('playing', onReady)
    onReady()
  })
}

async function loadImageElementFromSource(source) {
  if (source instanceof HTMLVideoElement) {
    const canvas = captureVideoFrameCanvas(source)
    if (!canvas) throw new Error('Could not capture video frame')
    return await new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = () => reject(new Error('Failed to load image'))
      image.src = canvas.toDataURL('image/jpeg', 0.92)
    })
  }
  if (source instanceof HTMLCanvasElement) {
    return await new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = () => reject(new Error('Failed to load image'))
      image.src = source.toDataURL('image/jpeg', 0.92)
    })
  }
  const objectUrl = URL.createObjectURL(source)
  try {
    return await new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = () => reject(new Error('Failed to load image'))
      image.src = objectUrl
    })
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

async function decodeQrFromImageSource(source) {
  const image = await loadImageElementFromSource(source)
  const maxSize = 1200
  let width = image.width
  let height = image.height
  if (width > maxSize || height > maxSize) {
    const scale = maxSize / Math.max(width, height)
    width = Math.round(width * scale)
    height = Math.round(height * scale)
  }
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''
  ctx.drawImage(image, 0, 0, width, height)
  return decodeQrFromCanvas(canvas)
}

function extractCbeTransactionIdFromQrContent(content) {
  if (!content) return ''
  const text = String(content).trim()

  const urlIdMatch = text.match(/[?&]id=([^&\s#]+)/i)
  if (urlIdMatch?.[1]) {
    const idValue = decodeURIComponent(urlIdMatch[1]).toUpperCase()
    const withSuffix = idValue.match(/^(FT[A-Z0-9]{8,12})(\d{8})$/)
    if (withSuffix?.[1] && isValidCbeTxnValue(withSuffix[1])) {
      return withSuffix[1]
    }
    if (isValidCbeTxnValue(idValue)) {
      return idValue
    }
    const candidate = pickCbeTxnCandidate(idValue)
    if (candidate) return candidate
  }

  const receiptMatch = text.match(/receipt\s*no\.?\s*[:-]?\s*(FT[A-Z0-9]{8,16})/i)
  if (receiptMatch?.[1] && isValidCbeTxnValue(receiptMatch[1])) {
    return cleanTxnId(receiptMatch[1]).toUpperCase()
  }

  return pickCbeTxnCandidate(text)
}

function extractTransactionIdFromQrContent(content, paymentMethod = null) {
  if (!content) return ''
  const name = normalizePaymentMethodName(paymentMethod)
  if (isCbeMethod(name)) {
    return extractCbeTransactionIdFromQrContent(content)
  }
  return ''
}

async function preprocessImageForOcr(source) {
  let objectUrl = null
  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = () => reject(new Error('Failed to load image'))
      if (source instanceof HTMLCanvasElement) {
        image.src = source.toDataURL('image/jpeg', 0.92)
        return
      }
      if (source instanceof Blob || source instanceof File) {
        objectUrl = URL.createObjectURL(source)
        image.src = objectUrl
        return
      }
      image.src = source
    })

    const maxDim = 1600
    let width = img.naturalWidth || img.width
    let height = img.naturalHeight || img.height
    if (!width || !height) throw new Error('Invalid image dimensions')
    if (width > maxDim || height > maxDim) {
      const ratio = Math.min(maxDim / width, maxDim / height)
      width = Math.round(width * ratio)
      height = Math.round(height * ratio)
    }

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas not supported')
    ctx.drawImage(img, 0, 0, width, height)

    const imageData = ctx.getImageData(0, 0, width, height)
    const pixels = imageData.data
    for (let i = 0; i < pixels.length; i += 4) {
      const gray = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2]
      const boosted = gray < 128 ? gray * 0.75 : Math.min(255, gray * 1.25)
      pixels[i] = pixels[i + 1] = pixels[i + 2] = boosted
    }
    ctx.putImageData(imageData, 0, 0)

    return canvas
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl)
  }
}

async function getTesseractWorker() {
  if (!tesseractWorker) {
    const { createWorker } = await import('tesseract.js')
    tesseractWorker = await createWorker('eng', 1, { logger: () => {} })
    await tesseractWorker.setParameters({
      tessedit_pageseg_mode: '3',
      preserve_interword_spaces: '1',
    })
  }
  return tesseractWorker
}

async function terminateTesseractWorker() {
  if (tesseractWorker) {
    await tesseractWorker.terminate()
    tesseractWorker = null
  }
}

function ensureMediaDevices() {
  if (typeof navigator === 'undefined') return false
  if (!navigator.mediaDevices) navigator.mediaDevices = {}
  if (!navigator.mediaDevices.getUserMedia) {
    const legacy =
      navigator.getUserMedia ||
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia
    if (legacy) {
      navigator.mediaDevices.getUserMedia = (constraints) =>
        new Promise((resolve, reject) => legacy.call(navigator, constraints, resolve, reject))
    }
  }
  return typeof navigator.mediaDevices.getUserMedia === 'function'
}

function canUseLiveCamera() {
  return typeof window !== 'undefined' && window.isSecureContext && ensureMediaDevices()
}

function isMobileDevice() {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '')
}

function getCameraErrorMessage(err) {
  if (!err) return 'Could not access camera.'
  if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
    return 'Camera permission denied. Allow camera access in your browser settings.'
  }
  if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
    return 'No camera found on this device.'
  }
  if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
    return 'Camera is busy or unavailable. Close other apps using the camera and try again.'
  }
  return err.message || 'Could not access camera.'
}

async function requestTxnCameraStream() {
  const constraintsList = [
    { video: { facingMode: { ideal: 'environment' } }, audio: false },
    { video: { facingMode: 'environment' }, audio: false },
    { video: { facingMode: 'user' }, audio: false },
    { video: true, audio: false },
  ]
  let lastError = null
  for (const constraints of constraintsList) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints)
    } catch (err) {
      lastError = err
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') break
    }
  }
  throw lastError || new Error('Could not access camera.')
}

async function recognizeTransactionFromLiveFrame(worker, canvas, paymentMethod) {
  let imageSource = canvas
  try {
    imageSource = await preprocessImageForOcr(canvas)
  } catch (err) {
    console.warn('Live OCR preprocess skipped:', err)
  }

  const psmModes = ['6', '11', '3']
  for (const psm of psmModes) {
    await worker.setParameters({
      tessedit_pageseg_mode: psm,
      preserve_interword_spaces: '1',
    })
    const scanResult = await worker.recognize(imageSource)
    const ocrText = scanResult.data.text
    const mismatchMessage = detectPaymentScreenshotMismatch(ocrText, paymentMethod)
    if (mismatchMessage) continue
    const extracted = extractTransactionIdFromOcrText(ocrText, paymentMethod)
    if (extracted && isTxnIdValidForPaymentMethod(extracted, paymentMethod)) {
      await worker.setParameters({ tessedit_pageseg_mode: '3', preserve_interword_spaces: '1' })
      return { extracted, receiverDisplay: extractScannedReceiverDisplay(ocrText, paymentMethod), error: '' }
    }
  }

  await worker.setParameters({ tessedit_pageseg_mode: '3', preserve_interword_spaces: '1' })
  return { extracted: '', receiverDisplay: '', error: '' }
}

function captureVideoFrameCanvas(video) {
  const width = video.videoWidth
  const height = video.videoHeight
  if (!width || !height) return null

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.drawImage(video, 0, 0, width, height)
  return canvas
}

function stopLiveTxnScanLoop() {
  if (txnLiveScanTimer) {
    clearInterval(txnLiveScanTimer)
    txnLiveScanTimer = null
  }
  if (txnQrScanRafId) {
    cancelAnimationFrame(txnQrScanRafId)
    txnQrScanRafId = null
  }
  txnLiveScanBusy = false
  txnLiveScanProcessing.value = false
}

function stopTxnLiveCameraHardware() {
  stopLiveTxnScanLoop()
  stopTxnCamera()
  showTxnLiveCameraScan.value = false
}

function stopLiveTxnCameraScan() {
  stopTxnLiveCameraHardware()
  txnLiveScanPurpose.value = 'ocr'
  txnPendingQrDecode = false
}

function closeLiveTxnCameraScan() {
  stopLiveTxnCameraScan()
}

function startLiveTxnScanLoop() {
  stopLiveTxnScanLoop()
  if (txnLiveScanPurpose.value === 'qr') {
    let lastTick = 0
    const qrLoop = (timestamp) => {
      if (!showTxnLiveCameraScan.value || txnLiveScanPurpose.value !== 'qr') return
      if (timestamp - lastTick >= 180) {
        lastTick = timestamp
        runLiveTxnScanTick().catch(() => {})
      }
      txnQrScanRafId = requestAnimationFrame(qrLoop)
    }
    runLiveTxnScanTick().catch(() => {})
    txnQrScanRafId = requestAnimationFrame(qrLoop)
    return
  }
  const tick = () => {
    runLiveTxnScanTick().catch(() => {})
  }
  tick()
  txnLiveScanTimer = setInterval(tick, 1400)
}

async function runLiveTxnScanTick() {
  if (txnLiveScanBusy || !showTxnLiveCameraScan.value) return

  const video = txnLiveScanVideo.value
  if (!video || !video.videoWidth) return

  txnLiveScanBusy = true
  txnLiveScanProcessing.value = true
  try {
    if (txnLiveScanPurpose.value === 'qr') {
      const qrContent = await decodeQrFromVideoFrame(video)
      if (qrContent) {
        const customResult = onQrContent ? await onQrContent(qrContent) : null
        const extracted = customResult?.extracted || extractTransactionIdFromQrContent(qrContent, getPaymentMethod())
        if (extracted && isTxnIdValidForPaymentMethod(extracted, getPaymentMethod())) {
          applyExtractedPaymentScan(extracted, customResult?.receiverDisplay || '')
          showToast('Transaction ID extracted from QR code', 'success')
          stopLiveTxnCameraScan()
          closeTxnScanModal()
        }
      }
      return
    }

    const canvas = captureVideoFrameCanvas(video)
    if (!canvas) return

    const worker = await getTesseractWorker()
    const { extracted, receiverDisplay } = await recognizeTransactionFromLiveFrame(worker, canvas, getPaymentMethod())
    if (extracted) {
      applyExtractedPaymentScan(extracted, receiverDisplay)
      showToast('Transaction ID extracted successfully', 'success')
      stopLiveTxnCameraScan()
      closeTxnScanModal()
    }
  } catch (err) {
    console.warn('Live transaction scan tick failed:', err)
  } finally {
    txnLiveScanBusy = false
    if (showTxnLiveCameraScan.value) {
      txnLiveScanProcessing.value = false
    }
  }
}

async function startLiveTxnCameraScan(purpose = 'ocr') {
  if (isScanningTransaction.value || showTxnLiveCameraScan.value) return

  if (!canUseLiveCamera()) {
    txnLiveScanPurpose.value = purpose
    if (isMobileDevice()) {
      txnPendingQrDecode = purpose === 'qr'
      openNativeTxnCamera()
      return
    }
    const insecure = typeof window !== 'undefined' && !window.isSecureContext
    showToast(
      insecure
        ? 'Live camera needs HTTPS. Use Gallery or open this page over a secure connection.'
        : 'Camera is not supported on this browser.',
      'error'
    )
    return
  }

  try {
    stopTxnLiveCameraHardware()
    txnLiveScanPurpose.value = purpose
    if (purpose === 'qr') await getJsQr()
    else getTesseractWorker().catch(() => {})

    showTxnLiveCameraScan.value = true
    await nextTick()
    txnScanCameraStream = await requestTxnCameraStream()
    await nextTick()

    const video = txnLiveScanVideo.value
    if (!video) throw new Error('Camera preview is not ready. Please try again.')
    video.setAttribute('playsinline', 'true')
    video.setAttribute('webkit-playsinline', 'true')
    video.srcObject = txnScanCameraStream
    await video.play().catch(() => {})
    await waitForTxnVideoReady(video)
    startLiveTxnScanLoop()
  } catch (err) {
    console.error('Live camera scan failed:', err)
    const failedPurpose = purpose
    stopLiveTxnCameraScan()
    if (isMobileDevice()) {
      txnLiveScanPurpose.value = failedPurpose
      txnPendingQrDecode = failedPurpose === 'qr'
      openNativeTxnCamera()
      return
    }
    showToast(getCameraErrorMessage(err), 'error')
  }
}

function openNativeTxnCamera() {
  txnScanCameraInput.value?.click()
}

function stopTxnCamera() {
  if (txnScanCameraStream) {
    txnScanCameraStream.getTracks().forEach((track) => track.stop())
    txnScanCameraStream = null
  }
  if (txnScanVideo.value) txnScanVideo.value.srcObject = null
  if (txnLiveScanVideo.value) txnLiveScanVideo.value.srcObject = null
}

function closeTxnScanSourcePicker() {
  showTxnScanSourcePicker.value = false
}

function pickTxnCameraFromSourcePicker() {
  closeTxnScanSourcePicker()
  startLiveTxnCameraScan('ocr')
}

function pickTxnGalleryFromSourcePicker() {
  closeTxnScanSourcePicker()
  txnPendingQrDecode = false
  pickTxnScreenshot()
}

function pickTxnQrFromSourcePicker() {
  closeTxnScanSourcePicker()
  startLiveTxnCameraScan('qr')
}

function closeTxnScanModal() {
  stopLiveTxnCameraScan()
  stopTxnCamera()
  showTxnScanModal.value = false
  txnScanMode.value = 'chooser'
}

function pickTxnScreenshot() {
  txnScanFileInput.value?.click()
}

function openTxnScanPicker() {
  if (isScanningTransaction.value) return
  getTesseractWorker().catch(() => {})
  if (isMobileDevice() || isCbePaymentSelected.value || isTelebirrPaymentSelected.value) {
    showTxnScanSourcePicker.value = true
    return
  }
  pickTxnScreenshot()
}

async function recognizeTransactionFromImage(worker, source, paymentMethod) {
  const imageSources = []
  try {
    const processedCanvas = await preprocessImageForOcr(source)
    imageSources.push(processedCanvas)
  } catch (err) {
    console.warn('OCR preprocess skipped:', err)
  }

  if (source instanceof File || source instanceof Blob || source instanceof HTMLCanvasElement) {
    if (!imageSources.includes(source)) imageSources.push(source)
  }

  const psmModes = ['3', '6', '11']
  let lastMismatchMessage = ''
  let receiverDisplay = ''
  for (const imageSource of imageSources) {
    for (const psm of psmModes) {
      await worker.setParameters({
        tessedit_pageseg_mode: psm,
        preserve_interword_spaces: '1',
      })
      const scanResult = await worker.recognize(imageSource)
      const ocrText = scanResult.data.text
      const mismatchMessage = detectPaymentScreenshotMismatch(ocrText, paymentMethod)
      if (mismatchMessage) {
        lastMismatchMessage = mismatchMessage
        continue
      }
      if (!receiverDisplay) {
        receiverDisplay = extractScannedReceiverDisplay(ocrText, paymentMethod)
      }
      const extracted = extractTransactionIdFromOcrText(ocrText, paymentMethod)
      if (extracted && isTxnIdValidForPaymentMethod(extracted, paymentMethod)) {
        await worker.setParameters({ tessedit_pageseg_mode: '3', preserve_interword_spaces: '1' })
        return {
          extracted,
          receiverDisplay: extractScannedReceiverDisplay(ocrText, paymentMethod) || receiverDisplay,
          error: '',
        }
      }
    }
  }

  await worker.setParameters({ tessedit_pageseg_mode: '3', preserve_interword_spaces: '1' })
  return { extracted: '', receiverDisplay, error: lastMismatchMessage }
}

async function processTxnQrScanImage(source) {
  isScanningTransaction.value = true
  try {
    const qrContent = await decodeQrFromImageSource(source)
    if (!qrContent) throw new Error('No QR code found in the image. Try again with a clearer image.')
    const extracted = extractTransactionIdFromQrContent(qrContent, getPaymentMethod())
    if (!extracted) throw new Error(getTxnQrScanErrorMessage(getPaymentMethod()))
    if (!isTxnIdValidForPaymentMethod(extracted, getPaymentMethod())) {
      throw new Error('QR code does not contain a valid transaction ID for the selected payment method.')
    }
    applyExtractedPaymentScan(extracted)
    showToast('Transaction ID extracted from QR code', 'success')
    closeTxnScanModal()
    closeLiveTxnCameraScan()
  } catch (err) {
    console.error('QR scan failed:', err)
    showToast(err.message || 'QR scan failed. Try again.', 'error')
  } finally {
    isScanningTransaction.value = false
    txnLiveScanPurpose.value = 'ocr'
    txnPendingQrDecode = false
  }
}

async function processTxnScanImage(source) {
  isScanningTransaction.value = true
  scannedReceiverDisplay.value = ''
  try {
    const name = normalizePaymentMethodName(getPaymentMethod())
    let extractedFromQr = ''
    if (isCbeMethod(name)) {
      try {
        const qrContent = await decodeQrFromImageSource(source)
        if (qrContent) {
          const qrExtracted = extractTransactionIdFromQrContent(qrContent, getPaymentMethod())
          if (qrExtracted && isTxnIdValidForPaymentMethod(qrExtracted, getPaymentMethod())) {
            extractedFromQr = qrExtracted
          }
        }
      } catch (qrErr) {
        console.warn('QR decode skipped, falling back to OCR:', qrErr)
      }
    }

    const worker = await getTesseractWorker()
    const { extracted, error, receiverDisplay } = await recognizeTransactionFromImage(worker, source, getPaymentMethod())
    const finalId = extractedFromQr || extracted

    if (!finalId) {
      if (error) throw new Error(error)
      throw new Error(getTxnScanErrorMessage(getPaymentMethod()))
    }

    applyExtractedPaymentScan(finalId, receiverDisplay)
    showToast(
      extractedFromQr ? 'Transaction ID extracted from QR code' : 'Transaction ID extracted successfully',
      'success'
    )
    closeTxnScanModal()
  } catch (err) {
    console.error('Transaction scan failed:', err)
    showToast(err.message || 'Scan failed. Try again with a clearer image.', 'error')
  } finally {
    isScanningTransaction.value = false
  }
}

async function handleTxnScanFile(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  if (txnPendingQrDecode || txnLiveScanPurpose.value === 'qr') {
    await processTxnQrScanImage(file)
    return
  }
  await processTxnScanImage(file)
}

async function startTxnCamera() {
  await startLiveTxnCameraScan()
}

async function captureAndScanTransaction() {
  const video = txnScanVideo.value
  if (!video || !video.videoWidth) {
    showToast('Camera is not ready yet. Please wait a moment.', 'error')
    return
  }
  const canvas = document.createElement('canvas')
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    showToast('Could not capture camera frame.', 'error')
    return
  }
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
  await processTxnScanImage(canvas)
}
  function cleanupPaymentTxnScan() {
    stopLiveTxnCameraScan()
    stopTxnCamera()
    terminateTesseractWorker()
  }

  return {
    scannedReceiverDisplay,
    isCbePaymentSelected,
    showTxnScanModal,
    showTxnScanSourcePicker,
    showTxnLiveCameraScan,
    isScanningTransaction,
    txnLiveScanProcessing,
    txnLiveScanPurpose,
    txnScanMode,
    txnScanFileInput,
    txnScanCameraInput,
    txnScanVideo,
    txnLiveScanVideo,
    openTxnScanPicker,
    closeTxnScanModal,
    closeTxnScanSourcePicker,
    closeLiveTxnCameraScan,
    pickTxnCameraFromSourcePicker,
    pickTxnGalleryFromSourcePicker,
    pickTxnQrFromSourcePicker,
    pickTxnScreenshot,
    startTxnCamera,
    captureAndScanTransaction,
    handleTxnScanFile,
    stopTxnCamera,
    cleanupPaymentTxnScan,
  }
}
